import React from 'react';

type DigitCodeInputProps = {
  /**
   * Number of digits in the code
   */
  length: number;
  /**
   * Current value of each digit
   */
  value: string[];
  /**
   * Callback when value changes
   */
  onChange: (value: string[]) => void;
  /**
   * Prefix for input IDs
   */
  prefix: string;
  /**
   * Optional callback when the last digit is entered
   */
  onComplete?: () => void;
  /**
   * Optional callback when backspace is pressed on the first field
   */
  onBackspaceFirstField?: () => void;
};

/**
 * A reusable component for digit code input fields (PIN, OTP, etc.)
 */
const DigitCodeInput: React.FC<DigitCodeInputProps> = ({ 
  length, 
  value, 
  onChange, 
  prefix,
  onComplete,
  onBackspaceFirstField
}) => {
  // Handle code change
  const handleCodeChange = (index: number, digit: string) => {
    // Allow only digits or empty value
    if (digit.length <= 1 && /^\d*$/.test(digit)) {
      const newValue = [...value];
      newValue[index] = digit;
      onChange(newValue);
      
      if (digit === '') return;
      
      // Check if this is the last digit and all fields are filled
      if (index === length - 1 && newValue.every(d => d !== '')) {
        onComplete?.();
      } else if (index < length - 1) {
        // Auto focus next input
        const nextInput = document.getElementById(`${prefix}-${index + 1}`);
        nextInput?.focus();
      }
    }
  };
  
  // Handle keydown events
  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    // Handle backspace key
    if (e.key === 'Backspace') {
      const newValue = [...value];
      
      // If current field is empty or cursor at the beginning of input
      if (value[index] === '' || (e.currentTarget as HTMLInputElement).selectionStart === 0) {
        // Prevent default behavior
        e.preventDefault();
        
        // Clear current field
        newValue[index] = '';
        onChange(newValue);
        
        // Move to previous field if not at the first field
        if (index > 0) {
          const prevInput = document.getElementById(`${prefix}-${index - 1}`);
          prevInput?.focus();
        } else if (index === 0 && onBackspaceFirstField) {
          onBackspaceFirstField();
        }
      }
    }
  };
  
  // Handle focus to select all text
  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    e.target.select();
  };

  return (
    <div className="flex gap-2 justify-between">
      {Array.from({ length }).map((_, index) => (
        <input
          key={`${prefix}-${index}`}
          id={`${prefix}-${index}`}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={value[index] || ''}
          onChange={(e) => handleCodeChange(index, e.target.value)}
          onFocus={handleFocus}
          onKeyDown={(e) => handleKeyDown(index, e)}
          className="font-bold w-[46px] h-[50px] text-center border border-[#C4C4C4] rounded-[15px] text-lg focus:outline-none focus:border-[#5A4099]"
        />
      ))}
    </div>
  );
};

export default DigitCodeInput;
